import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import {
    ChartBarIcon,
    UserGroupIcon,
    SparklesIcon,
    CalendarIcon,
    ClipboardListIcon,
    LightningBoltIcon,
} from "@heroicons/react/outline";
import heroImage from "../../assets/hero-image.jpeg";
import logo from "../../assets/cellies.png";
import "./Home.css";
import { LegalLinks } from "../Legal/LegalLayout";

const featureCards = [
    {
        icon: ClipboardListIcon,
        title: "Structured Reporting",
        description:
            "Create weekly reports quickly with focused sections for members, guests, testimonies, and evaluations.",
    },
    {
        icon: UserGroupIcon,
        title: "Member-First Tracking",
        description:
            "Track active members and guest participation with cleaner attendance workflows and better visibility.",
    },
    {
        icon: ChartBarIcon,
        title: "Attendance Insights",
        description:
            "Review trends over time with history charts and performance signals that help leaders act faster.",
    },
    {
        icon: CalendarIcon,
        title: "Weekly Rhythm",
        description:
            "Align meetings, follow-ups, and updates in one place so teams stay consistent every week.",
    },
];

function Home() {
    const pageRef = useRef(null);
    const spotlightRef = useRef(null);
    const visualRef = useRef(null);

    useEffect(() => {
        const container = pageRef.current;
        const spotlight = spotlightRef.current;
        const visual = visualRef.current;

        if (!container || !spotlight || !visual) {
            return undefined;
        }

        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const ctx = gsap.context(() => {
            if (prefersReducedMotion) {
                gsap.set(
                    [
                        ".landing-nav",
                        ".hero-title-line",
                        ".hero-subtitle",
                        ".hero-actions",
                        ".hero-metric",
                        ".hero-visual",
                        ".feature-card",
                        ".landing-cta",
                    ],
                    { autoAlpha: 1, y: 0, x: 0, rotate: 0 }
                );
                return;
            }

            gsap.set(".landing-spotlight", { xPercent: -50, yPercent: -50, autoAlpha: 0.65 });

            const intro = gsap.timeline({ defaults: { ease: "power3.out" } });
            intro
                .fromTo(".landing-nav", { autoAlpha: 0, y: -20 }, { autoAlpha: 1, y: 0, duration: 0.7 })
                .fromTo(".hero-title-line", { autoAlpha: 0, y: 48 }, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.12 }, "-=0.2")
                .fromTo(".hero-subtitle", { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.55 }, "-=0.25")
                .fromTo(".hero-actions", { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.55 }, "-=0.2")
                .fromTo(".hero-metric", { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.45, stagger: 0.1 }, "-=0.2")
                .fromTo(".hero-visual", { autoAlpha: 0, x: 32, rotate: 1 }, { autoAlpha: 1, x: 0, rotate: 0, duration: 0.8 }, "-=0.4")
                .fromTo(".feature-card", { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.1 }, "-=0.2")
                .fromTo(".landing-cta", { autoAlpha: 0, y: 28 }, { autoAlpha: 1, y: 0, duration: 0.55 }, "-=0.15");

            gsap.utils.toArray(".float-chip").forEach((chip, index) => {
                gsap.to(chip, {
                    y: index % 2 === 0 ? -12 : 12,
                    duration: 2.1 + index * 0.35,
                    repeat: -1,
                    yoyo: true,
                    ease: "sine.inOut",
                });
            });
        }, container);

        const moveSpotlight = (event) => {
            const bounds = container.getBoundingClientRect();
            const x = event.clientX - bounds.left;
            const y = event.clientY - bounds.top;
            const rotateY = ((x / bounds.width) - 0.5) * 8;
            const rotateX = ((y / bounds.height) - 0.5) * -6;

            gsap.to(spotlight, { x, y, duration: 0.35, ease: "power2.out" });
            if (!prefersReducedMotion) {
                gsap.to(visual, { rotateY, rotateX, transformPerspective: 900, duration: 0.45, ease: "power2.out" });
            }
        };

        const resetVisual = () => {
            gsap.to(visual, { rotateY: 0, rotateX: 0, duration: 0.45, ease: "power2.out" });
        };

        container.addEventListener("pointermove", moveSpotlight);
        container.addEventListener("pointerleave", resetVisual);

        return () => {
            container.removeEventListener("pointermove", moveSpotlight);
            container.removeEventListener("pointerleave", resetVisual);
            ctx.revert();
        };
    }, []);

    useEffect(() => {
        if (window.location.hash !== "#features") {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            const featuresSection = document.getElementById("features");
            if (featuresSection) {
                featuresSection.scrollIntoView({ behavior: "auto", block: "start" });
            }
        }, 0);

        return () => window.clearTimeout(timer);
    }, []);

    const handleScrollToFeatures = (event) => {
        event.preventDefault();
        const featuresSection = document.getElementById("features");
        if (!featuresSection) {
            return;
        }

        featuresSection.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div ref={pageRef} className="landing-shell">
            <div className="landing-orb landing-orb-one" />
            <div className="landing-orb landing-orb-two" />
            <div className="landing-orb landing-orb-three" />
            <div ref={spotlightRef} className="landing-spotlight" />

            <div className="landing-content">
                <header className="landing-nav">
                    <div className="brand-lockup">
                        <img src={logo} alt="Cell Report logo" className="brand-logo" />
                        <div>
                            <p className="brand-caption">ANFGC Halifax</p>
                            <h1 className="brand-title">Cell Report</h1>
                        </div>
                    </div>
                    <div className="nav-actions">
                        <a className="nav-link" href="#features" onClick={handleScrollToFeatures}>
                            Explore
                        </a>
                        <Link to="/login" className="button-primary">
                            Login
                        </Link>
                    </div>
                </header>

                <main className="landing-main">
                    <section className="hero-grid">
                    <div className="hero-copy">
                        <p className="hero-kicker">
                            <SparklesIcon className="hero-kicker-icon" />
                            Built for modern cell leadership
                        </p>
                        <h2 className="hero-title">
                            <span className="hero-title-line">Lead Every Week</span>
                            <span className="hero-title-line">With Clear Insight</span>
                            <span className="hero-title-line">and Less Friction</span>
                        </h2>
                        <p className="hero-subtitle">
                            Cell Report helps leaders organize attendance, track growth, and submit reports with a clean flow
                            designed for real ministry operations.
                        </p>
                        <div className="hero-actions">
                            <Link to="/login" className="button-primary hero-button">
                                Start Reporting
                            </Link>
                            <a href="#features" className="button-secondary hero-button" onClick={handleScrollToFeatures}>
                                View Platform Features
                            </a>
                        </div>

                        <div className="hero-metrics">
                            <article className="hero-metric">
                                <p className="metric-value">Weekly</p>
                                <p className="metric-label">Attendance snapshots</p>
                            </article>
                            <article className="hero-metric">
                                <p className="metric-value">Live</p>
                                <p className="metric-label">Team-level visibility</p>
                            </article>
                            <article className="hero-metric">
                                <p className="metric-value">Fast</p>
                                <p className="metric-label">Report submission flow</p>
                            </article>
                        </div>
                    </div>

                    <div ref={visualRef} className="hero-visual">
                        <img src={heroImage} alt="Cell meeting collaboration" className="hero-image" />
                        <div className="float-chip chip-a">
                            <LightningBoltIcon className="chip-icon" />
                            Faster check-ins
                        </div>
                        <div className="float-chip chip-b">
                            <ChartBarIcon className="chip-icon" />
                            Attendance trends
                        </div>
                        <div className="float-chip chip-c">
                            <UserGroupIcon className="chip-icon" />
                            Active member focus
                        </div>
                    </div>
                </section>

                    <section id="features" className="feature-section">
                        <div className="feature-heading">
                            <p className="feature-kicker">Platform capabilities</p>
                            <h3>Everything you need to run healthy, measurable cell operations</h3>
                        </div>
                        <div className="feature-grid">
                            {featureCards.map((feature) => {
                                const Icon = feature.icon;
                                return (
                                    <article key={feature.title} className="feature-card">
                                        <div className="feature-icon-wrap">
                                            <Icon className="feature-icon" />
                                        </div>
                                        <h4>{feature.title}</h4>
                                        <p>{feature.description}</p>
                                    </article>
                                );
                            })}
                        </div>
                    </section>

                    <section className="landing-cta">
                        <h3>Ready to streamline your next cell report?</h3>
                        <p>Sign in and run your weekly reporting workflow with cleaner tracking and better visibility.</p>
                        <Link to="/login" className="button-primary">
                            Go to Login
                        </Link>
                    </section>
                </main>
                <footer className="flex flex-col items-center gap-2 px-4 py-8 text-sm text-gray-500 sm:flex-row sm:justify-between sm:px-8">
                    <p>© {new Date().getFullYear()} ANFGC Halifax</p>
                    <LegalLinks />
                </footer>
            </div>
        </div>
    );
}

export default Home;
